import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, SettingsIcon } from 'lucide-react';
import { useMemo } from 'react';
import {
  Button,
  Checkbox,
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../index';
import { useColumnPersistence } from './hooks/useColumnPersistence';
import { useColumnSelector } from './hooks/useColumnSelector';
import { ColumnSelectorProps } from './types';

function SortableOrderRow({ id, label }: { id: string; label: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-2 py-1.5 text-sm text-foreground"
    >
      <button
        type="button"
        className="touch-none shrink-0 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Arrastrar para reordenar"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </div>
  );
}

export const ColumnSelector = <T,>({
  columns,
  fixedColumns = [],
  onColumnsChange,
  selected,
  dataTestId,
  storageKey,
  reorderable = false,
  reorderSectionTitle = 'Orden de columnas',
}: ColumnSelectorProps<T>) => {
  const { availableColumns, isColumnSelected, isColumnFixed, saveColumnPreferences } = useColumnSelector({
    columns,
    fixedColumns,
    selected,
    storageKey,
  });

  useColumnPersistence(storageKey, availableColumns, onColumnsChange);

  const labelById = useMemo(
    () => Object.fromEntries(availableColumns.map((c) => [c.id, c.label])),
    [availableColumns]
  );

  const fixedHead = useMemo(
    () => fixedColumns.filter((id) => selected.includes(id)),
    [fixedColumns, selected]
  );

  const sortableTail = useMemo(
    () => selected.filter((id) => !fixedColumns.includes(id)),
    [selected, fixedColumns]
  );

  const showReorderBlock = reorderable && sortableTail.length > 1;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortableTail.indexOf(String(active.id));
    const newIndex = sortableTail.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const newTail = arrayMove(sortableTail, oldIndex, newIndex);
    const newSelected = [...fixedHead, ...newTail];
    if (storageKey) saveColumnPreferences(newSelected);
    onColumnsChange(newSelected);
  };

  // Togglear selección de columna
  const handleToggleColumn = (columnId: string) => {
    if (!selected) return;
    const isSelected = selected.includes(columnId);
    let newSelected: string[];
    if (isSelected) {
      // No permitir deseleccionar si solo queda una columna no fija
      const nonFixedSelected = selected.filter((id) => !fixedColumns.includes(id));
      if (nonFixedSelected.length <= 1) return;
      newSelected = selected.filter((id) => id !== columnId);
    } else {
      newSelected = [...selected, columnId];
    }

    if (storageKey) {
      saveColumnPreferences(newSelected);
    }

    onColumnsChange(newSelected);
  };

  return (
    <Popover data-testid={dataTestId}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-12 h-8 border border-input text-foreground/70 hover:text-foreground hover:bg-accent"
          data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
        >
          <SettingsIcon size={16} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto min-w-[280px] border border-card-border !bg-body-bg rounded-lg p-0">
        <div className="flex flex-col">
          <Command className="!bg-transparent">
            <CommandInput placeholder="Buscar columna" />
            <CommandList>
              {availableColumns
                .filter((column) => column.label)
                .map((column) => (
                  <CommandItem
                    key={column.id}
                    className="flex items-center gap-2 data-[selected=true]:bg-hover-press"
                  >
                    <Checkbox
                      className="dark:data-[state=checked]:bg-title dark:text-body-bg light:data-[state=checked]:!bg-badge-status-4 light:!text-text light:data-[state=checked]:border-0"
                      checked={isColumnSelected(column.id)}
                      onCheckedChange={() => handleToggleColumn(column.id)}
                      disabled={isColumnFixed(column.id)}
                    />
                    {column.label}
                  </CommandItem>
                ))}
            </CommandList>
          </Command>

          {showReorderBlock && (
            <div className="border-t border-border px-3 py-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">{reorderSectionTitle}</p>
              {fixedHead.length > 0 && (
                <ul className="space-y-1.5 text-xs text-muted-foreground">
                  {fixedHead.map((id) => (
                    <li key={id} className="truncate rounded-md border border-border/40 bg-muted/20 px-2 py-1.5">
                      {labelById[id] ?? id}
                    </li>
                  ))}
                </ul>
              )}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={sortableTail} strategy={verticalListSortingStrategy}>
                  <div className="flex flex-col gap-1.5">
                    {sortableTail.map((id) => (
                      <SortableOrderRow key={id} id={id} label={labelById[id] ?? id} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Exportar tipos para facilitar el uso
export type { ColumnSelectorProps } from './types';
